import { CloudOff } from "lucide-react";
import * as React from "react";
import { Button } from "../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../primitives/dialog";
import { Checkbox, Input, Label } from "../primitives/input";
import { Callout } from "../primitives/callout";
import { CopyButton } from "./copy";

/**
 * Delete, Cancel and Stop-with-data-loss (ui-spec §4.8): names the object, says what is lost (paths read from the
 * object), shows the scoped CLI and, for deletes, requires typing the name (or "delete N" for bulk).
 */
export function ConfirmDestructive({
  open,
  onOpenChange,
  title,
  consequences,
  cli,
  confirmText,
  actionLabel,
  onConfirm,
  pending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  consequences: React.ReactNode;
  cli: string;
  /** The text the user must type; omitted for cancels and stops. */
  confirmText?: string;
  actionLabel: string;
  onConfirm: () => void;
  pending?: boolean;
}) {
  const [typed, setTyped] = React.useState("");
  const id = React.useId();
  React.useEffect(() => {
    if (!open) setTyped("");
  }, [open]);
  const ready = !confirmText || typed === confirmText;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* One column no wider than the dialog, so a long command scrolls inside its box instead of widening it. */}
      <DialogContent className="grid-cols-[minmax(0,1fr)]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div>{consequences}</div>
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-md border border-line bg-sunken px-3 py-2">
          <code tabIndex={0} className="min-w-0 flex-1 overflow-x-auto font-mono text-mono whitespace-nowrap text-fg-secondary">{cli}</code>
          <CopyButton text={cli} label="Copy command" />
        </div>
        {confirmText ? (
          <div className="grid gap-1.5">
            <Label htmlFor={id}>
              Type <code className="font-mono">{confirmText}</code> to confirm
            </Label>
            <Input id={id} value={typed} autoComplete="off" onChange={(e) => setTyped(e.target.value)} />
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" disabled={!ready || pending} onClick={onConfirm}>
            {actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Secrets, API keys, signing secrets and enrollment tokens are revealed once, with an "I've saved it" check before
 * Close, and never shown again (ui-spec §4.8).
 */
export function RevealOnce({
  open,
  title,
  secret,
  description,
  onDone,
}: {
  open: boolean;
  title: string;
  secret: string;
  description?: React.ReactNode;
  onDone: () => void;
}) {
  const [saved, setSaved] = React.useState(false);
  const id = React.useId();
  // The check starts empty for every secret this dialog reveals, not only the first.
  const done = () => {
    setSaved(false);
    onDone();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && saved && done()}>
      <DialogContent hideClose onEscapeKeyDown={(e) => !saved && e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description ?? "Copy it now. It is shown only once."}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-md border border-line bg-sunken px-3 py-2">
          <code data-testid="revealed-secret" className="min-w-0 flex-1 font-mono text-mono break-all text-fg-body">
            {secret}
          </code>
          <CopyButton text={secret} label="Copy" showLabel />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox id={id} checked={saved} onCheckedChange={(v) => setSaved(v === true)} />
          <Label htmlFor={id}>I&apos;ve saved it</Label>
        </div>
        <DialogFooter>
          <Button disabled={!saved} onClick={done}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Nothing wakes stopped, billable compute without this click (ui-spec §4.16). */
export function WakeConfirm({
  open,
  onOpenChange,
  name,
  rate,
  eta,
  actionLabel = "Wake and open",
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  rate: string;
  eta?: string;
  actionLabel?: string;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Wake {name}?</DialogTitle>
          <DialogDescription>
            Wakes {name} · ≈ {rate}
            {eta ? ` · about ${eta} to start` : ""}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>{actionLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A create or state change whose outcome is unknown (network error, timeout, 5xx): Check status by name, or Retry
 * with the same Idempotency-Key and body (ui-spec §4.17).
 */
export function UncertainOutcome({
  onCheck,
  onRetry,
  requestId,
  idempotencyKey,
}: {
  onCheck: () => void;
  onRetry: () => void;
  requestId?: string;
  idempotencyKey: string;
}) {
  return (
    <Callout
      tone="warning"
      role="alert"
      title="We couldn't confirm this"
      action={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCheck}>
            Check status
          </Button>
          <Button size="sm" onClick={onRetry}>
            Retry
          </Button>
        </div>
      }
    >
      <span className="inline-flex items-center gap-2">
        <CloudOff className="size-3.5" aria-hidden />
        The request may or may not have gone through. Retrying is safe: it reuses key{" "}
        <code className="font-mono">{idempotencyKey.slice(0, 8)}</code>
        {requestId ? (
          <>
            {" "}
            · request <code className="font-mono">{requestId}</code>
          </>
        ) : null}
      </span>
    </Callout>
  );
}
